import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { PopoverPosition } from '@blueprintjs/core';
import { isEmpty, isEqual } from 'lodash';
import { MAX_TRACE_ITEMS } from '@kentik/ui-shared/synthetics/constants';
import { Box, Card, EmptyState, Flex, Text, Icon, Button, Popover, Tag, Tooltip } from 'core/components';
import { Field, Form, GroupedMultiSelect, InputGroup, Select, Switch } from 'core/form';
import MultiSlider, { Handle } from 'core/components/MultiSlider';
import { safelyParseJSON } from 'core/util';
import AgentSelectorCompact from 'app/views/synthetics/components/agentSelector/AgentSelectorCompact';
import { BaseAgentRenderer } from 'app/views/synthetics/components/AgentRenderer';
import { FiInfo } from 'react-icons/fi';
import ToggleButtonGroup from 'core/components/ToggleButtonGroup';
import Traceroute from './Traceroute';
import TracerouteTable from './TracerouteTable';

const fields = {
  selectedAgents: {
    label: 'Show',
    defaultValue: []
  },
  selectedIps: {
    defaultValue: []
  },
  selectedAgent: {
    label: 'Agent',
    placeholder: 'Select an agent',
    defaultValue: ''
  },
  selectedIp: {
    label: 'Target',
    placeholder: 'Select a target',
    defaultValue: ''
  },
  groupBy: {
    label: 'Group hops by',
    options: [
      { label: 'No Grouping', value: 'none' },
      { label: 'ASN', value: 'asn' },
      { label: 'Site', value: 'site' }
    ],
    defaultValue: 'none'
  },
  percSuccessThreshold: {
    defaultValue: 0.9,
    rules: 'numeric',
    messages: { numeric: 'Not a valid number' },
    transform: {
      in: (value) => {
        if (value === '') {
          return 0;
        }
        return value * 100;
      },
      out: (value) => {
        if (value === '') {
          return 0;
        }
        return value / 100;
      }
    }
  },
  deltaLatencyThreshold: {
    defaultValue: 50,
    rules: 'numeric',
    messages: { numeric: 'Not a valid number' }
  },
  moreThanExpected: {
    label: 'Highlight links exceeding geo latency estimate',
    defaultValue: true
  },
  collapseFromStart: {
    label: 'After Agents',
    defaultValue: 0
  },
  collapseFromEnd: {
    label: 'Before Target',
    defaultValue: 0
  },
  openPaths: {
    defaultValue: null
  },
  collapseTimeouts: {
    label: 'Collapse Timeouts',
    defaultValue: true
  },
  viewMode: {
    defaultValue: 'graph'
  }
};

@inject('$app', '$exports', '$auth', '$dataviews', '$syn')
@Form({ fields, options: { name: 'Traceroute Options' } })
@observer
class TestPath extends Component {
  static defaultProps = {
    allowExport: true,
    isPreview: false
  };

  constructor(props) {
    super(props);

    const { $exports, form, allowExport, testResults } = this.props;

    if (allowExport) {
      $exports.getSettings().then(({ hashedTracerouteOpts }) => {
        let savedTracerouteOpts = {};

        if (hashedTracerouteOpts) {
          savedTracerouteOpts = hashedTracerouteOpts || {};
        } else if (window.localStorage) {
          savedTracerouteOpts = safelyParseJSON(localStorage.getItem('tracerouteOpts')) || {};
        }

        // Don't restore agent/target selections from saved state — they may be stale
        // and point to agents not in the current trace data. Only restore display preferences.
        const { selectedAgents, selectedIps, selectedAgent, selectedIp, ...displayOpts } = savedTracerouteOpts;
        form.setValues({ ...displayOpts });
      });
    }

    this._savedGraphAgents = null; // multi-select agents to restore on graph switch
    this._savedGraphIps = null; // multi-select targets to restore on graph switch

    // initialize selected agent ids and target ips
    // the target ips are a list of objects that provide an ip and optional agent id when available
    const initAgentIds = testResults.lastFetchedTraceParams.agentIds;
    const initTargetIps = testResults.lastFetchedTraceParams.selectedTargets.map(({ target_ip }) => target_ip) || [];

    // Synchronously read saved viewMode from localStorage to prevent flash on remount
    const savedTracerouteOptsSync = safelyParseJSON(localStorage.getItem('tracerouteOpts')) || {};
    const savedViewMode = savedTracerouteOptsSync.viewMode || 'graph';
    const savedSelectedIp = savedTracerouteOptsSync.selectedIp || '';
    const savedSelectedAgent = savedTracerouteOptsSync.selectedAgent || '';

    // In table mode, restore the saved single-select agent/target if they exist in the current data
    const useTableSelections = savedViewMode === 'table';

    let resolvedAgent = initAgentIds.length ? String(initAgentIds[0]) : '';
    if (useTableSelections && savedSelectedAgent && initAgentIds.includes(savedSelectedAgent)) {
      resolvedAgent = savedSelectedAgent;
    }

    let resolvedIp = initTargetIps.length ? initTargetIps[0] : '';
    if (useTableSelections && savedSelectedIp && initTargetIps.includes(savedSelectedIp)) {
      resolvedIp = savedSelectedIp;
    }

    form.setValues({
      viewMode: savedViewMode,
      selectedAgents: useTableSelections && resolvedAgent ? [resolvedAgent] : initAgentIds,
      selectedIps: useTableSelections && resolvedIp ? [resolvedIp] : initTargetIps,
      selectedAgent: resolvedAgent,
      selectedIp: resolvedIp
    });

    form.onChange = this.handleFormChange;
  }

  componentDidUpdate(prevProps) {
    const { form, traceroutes, testResults } = this.props;

    if (traceroutes !== prevProps.traceroutes) {
      const { agentIds } = testResults.lastFetchedTraceParams;
      const { viewMode } = form.getValues();

      if (viewMode === 'table') {
        // In table mode, only set agent/target if they're empty (initial load).
        // Preserve the user's current target selection when changing agents —
        // TracerouteTable handles the case where the selected target has no data.
        const { selectedIps, selectedAgents } = form.getValues();
        const updates = {};

        if (!selectedAgents.length && agentIds.length) {
          updates.selectedAgents = [String(agentIds[0])];
          updates.selectedAgent = String(agentIds[0]);
        }

        if (!selectedIps.length && traceroutes.length) {
          const firstTarget = this.getFirstAvailableTarget(traceroutes);
          if (firstTarget) {
            updates.selectedIps = [firstTarget];
            updates.selectedIp = firstTarget;
          }
        }

        if (Object.keys(updates).length) {
          form.setValues(updates);
        } else {
          // No updates needed — preserve current selection
        }
      } else {
        form.setValues({ selectedAgents: agentIds });
      }
    }
  }

  // Get the first available target IP from trace data
  getFirstAvailableTarget = (traceroutes) => {
    if (!traceroutes?.length) {
      return null;
    }
    const traceroute = traceroutes[0];
    if (!traceroute?.traces?.length) {
      return null;
    }
    return traceroute.traces[0]?.target?.target_ip || null;
  };

  handleAgentTargetClose = () => {
    const { form, testResults } = this.props;
    const { selectedAgents, selectedIps } = form.getValues();
    const lastFetchedAgentIds = testResults.lastFetchedTraceParams.agentIds;
    const lastFetchedTargetIps = testResults.lastFetchedTraceParams.selectedTargets.map(({ target_ip }) => target_ip);
    const agentsChanged = !isEqual(selectedAgents, lastFetchedAgentIds);
    const targetsChanged = !isEqual(selectedIps, lastFetchedTargetIps);
    const hasChangedData = agentsChanged || targetsChanged;

    if (hasChangedData && selectedAgents.length && (selectedIps.length || !this.targetOptions.length)) {
      // if selections have changed in either agents or targets AND we have something to fetch for
      // the logic that checks for blank target options covers a scenario where a test that relies on targets returned from the trace query that contains only offline agents can still re-fetch
      // run the trace results query again using the current date range and the currently selected agents and ips

      // Wipe cache since the user explicitly changed filter params
      testResults.traceResultsTs = {};
      testResults.lastFetchedTraceParams = { ...testResults.lastFetchedTraceParams, agentIds: [], targetIps: [] };
      testResults.fetchTraceResults({
        start_time: testResults.actualStartDate,
        end_time: testResults.actualEndDate,
        agent_ids: selectedAgents,
        target_ips: selectedIps
      });
    }
  };

  handleViewModeChange = (newMode) => {
    const { form } = this.props;
    const { selectedAgents, selectedIps } = form.getValues();

    if (newMode === 'table') {
      // Save current multi-select state for later restoration
      this._savedGraphAgents = selectedAgents;
      this._savedGraphIps = selectedIps;

      const firstAgent = selectedAgents.length ? selectedAgents[0] : '';
      const firstIp = selectedIps.length ? selectedIps[0] : '';

      // Auto-select first agent and first target for table mode
      form.setValues({
        viewMode: 'table',
        selectedAgent: firstAgent,
        selectedIp: firstIp,
        selectedAgents: firstAgent ? [firstAgent] : [],
        selectedIps: firstIp ? [firstIp] : []
      });
    } else {
      // Restore saved multi-select state
      const restoredAgents = this._savedGraphAgents || selectedAgents;
      const restoredIps = this._savedGraphIps || selectedIps;

      form.setValues({
        viewMode: 'graph',
        selectedAgents: restoredAgents,
        selectedIps: restoredIps
      });

      this._savedGraphAgents = null;
      this._savedGraphIps = null;
    }

    this.saveFormChange();
  };

  handleSingleAgentChange = ({ value }) => {
    const { form } = this.props;
    const agentStr = String(value);
    form.setValues({ selectedAgent: agentStr, selectedAgents: [agentStr] });

    const hasData = this.hasTraceDataFor([agentStr]);

    if (hasData) {
      this.saveFormChange();
    } else {
      this.fetchForAgent([agentStr]);
    }
  };

  handleSingleIpChange = ({ value }) => {
    const { form } = this.props;
    const { selectedAgents } = form.getValues();
    form.setValues({ selectedIp: value, selectedIps: [value] });

    if (this.hasTraceDataFor(null, [value])) {
      this.saveFormChange();
    } else {
      const { testResults } = this.props;
      testResults.fetchTraceResults({
        start_time: testResults.actualStartDate,
        end_time: testResults.actualEndDate,
        agent_ids: selectedAgents,
        target_ips: [value]
      });
    }
  };

  // Fetch trace results for a specific agent (all targets), appending to existing data
  fetchForAgent = (agentIds) => {
    const { testResults } = this.props;
    testResults.fetchTraceResults({
      start_time: testResults.actualStartDate,
      end_time: testResults.actualEndDate,
      agent_ids: agentIds,
      target_ips: []
    });
  };

  // Check whether the currently loaded traceroutes already contain data for the given agent/target
  hasTraceDataFor = (agentIds = null, targetIps = null) => {
    const { traceroutes } = this.props;
    if (!traceroutes || !traceroutes.length) {
      return false;
    }

    const traceroute = traceroutes[0];
    if (!traceroute?.traces) {
      return false;
    }

    return traceroute.traces.some(({ agentId, target }) => {
      const matchAgent = !agentIds || agentIds.includes(`${agentId}`);
      const matchTarget = !targetIps || (target?.target_ip != null && targetIps.includes(target.target_ip));
      return matchAgent && matchTarget;
    });
  };

  handleFormChange = () => this.saveFormChange();

  saveFormChange = () => {
    const { $exports, form, allowExport } = this.props;
    const values = form.getValues();

    if (allowExport) {
      if (window.localStorage) {
        localStorage.setItem('tracerouteOpts', JSON.stringify(values));
      }
      $exports.setHash({
        hashedTracerouteOpts: values
      });
    }
  };

  onGroupByChange = ({ value: groupBy }) => {
    const { form } = this.props;

    form.setValues({
      groupBy,
      collapseFromStart: 0,
      collapseFromEnd: 0
    });

    this.saveFormChange();
  };

  onOpenPaths = (agentID, target_ip) => {
    const { form } = this.props;
    const { openPaths } = form.getValues();

    form.setValues({
      openPaths: openPaths ? [...openPaths, { agentID, target_ip }] : [{ agentID, target_ip }]
    });

    this.saveFormChange();
  };

  closeOpenPaths = (agentID) => {
    const { form } = this.props;
    const { openPaths } = form.getValues();

    form.setValues({
      openPaths: openPaths ? openPaths.filter((path) => path.agentID !== agentID) : null
    });

    this.saveFormChange();
  };

  setPaths = (agentIDs) => {
    const { form } = this.props;
    const { openPaths } = form.getValues();

    form.setValues({
      openPaths: openPaths ? openPaths.filter((path) => agentIDs.find((agentID) => path.agentID === agentID)) : null
    });

    this.saveFormChange();
  };

  // HTTP and page load tests target a hostname/URL — the resolved IP varies per timestamp.
  // For these tests, don't filter traces by target IP.
  get isHostnameTargetedTest() {
    const { testResults } = this.props;
    const { test } = testResults;
    return !!(test && (test.isHttp || test.isPageLoad));
  }

  get isMeshTest() {
    const { testResults } = this.props;
    const { test } = testResults;
    return !!(test && test.isMesh);
  }

  // returns a list of select options that hydrate the agent selector
  get agentOptions() {
    const { $syn, agents } = this.props;
    let options = [];

    if (agents && agents.length > 0) {
      options = $syn.agents
        .getOptionsForAgentsByIds(agents)
        .map((agent) => ({
          ...agent,
          value: `${agent.value}`
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    return options;
  }

  /*
    Returns a list of select options that hydrate the target selector
    Select options support the following:
      - option values will always be an ip, but will support shipping agent options where available so we can render agents in the combo
  */
  get targetOptions() {
    const { $syn, testResults } = this.props;
    // allTargets contains the comprehensive list of ips returned from the last trace results fetch
    const { allTargets = [] } = testResults.lastFetchedTraceParams || {};

    return allTargets
      .map(({ target_ip, target_agent_id }) => {
        const agentOptions = $syn.agents.getOptionsForAgentsByIds([target_agent_id])[0]; // try to get agent options

        // For mesh tests, show agent name as the label instead of raw IP
        const label = this.isMeshTest && agentOptions?.label ? agentOptions.label : target_ip;

        return {
          label,
          value: target_ip,
          filterLabel: `${agentOptions?.label || ''}-${target_ip}`, // support searching by ip and agent label where available
          agentOptions
        };
      })
      .sort((a, b) => a.filterLabel.localeCompare(b.filterLabel));
  }

  /*
    Renderer for target options.
    All options are rendered as checkboxes with a target ip or an agent renderer
  */
  targetOptionFormatter = (opts) => {
    const { value, agentOptions } = opts;
    return (
      <Box ml={1} key={value} title={value}>
        {agentOptions ? (
          <BaseAgentRenderer {...agentOptions} locationInfoProps={{ ellipsis: true }} showLocationInfo small />
        ) : (
          value
        )}
      </Box>
    );
  };

  resetForm = () => {
    const { form, testResults } = this.props;

    form.reset();
    form.setValues({
      selectedAgents: testResults.lastFetchedTraceParams.agentIds,
      selectedIps: testResults.lastFetchedTraceParams.selectedTargets.map(({ target_ip }) => target_ip) || []
    });

    this.saveFormChange();
  };

  render() {
    const { testResults, traceroutes, useLocalIp, form, onNodeSelect, selectedNode, isPreview, $exports } = this.props;
    const { traceResults } = testResults;
    const { max_asn_path_count, max_site_path_count } = traceResults;

    const {
      deltaLatencyThreshold,
      collapseFromStart,
      collapseFromEnd,
      groupBy,
      openPaths,
      percSuccessThreshold,
      selectedAgents,
      selectedIps,
      collapseTimeouts,
      moreThanExpected,
      viewMode
    } = form.getValues();

    const { label: groupByLabel } = fields.groupBy.options.find((o) => o.value === groupBy);
    const groupBtnText =
      groupBy === 'none'
        ? 'None or ASN...' // 'ASN, Site...'
        : `${groupByLabel}: ${collapseFromStart} after agent, ${collapseFromEnd} before target`;
    const maxPathCount = (groupBy === 'asn' && max_asn_path_count) || (groupBy === 'site' && max_site_path_count) || 0;
    const hasResults = !isEmpty(traceResults);

    return (
      <Box>
        <Flex justifyContent="space-between" p={1} mb={2}>
          <Flex flexDirection="column" gap={2}>
            <ToggleButtonGroup selectedValue={viewMode}>
              <Button icon="graph" value="graph" onClick={() => this.handleViewModeChange('graph')}>
                Path
              </Button>
              <Button icon="th" value="table" onClick={() => this.handleViewModeChange('table')}>
                Table
              </Button>
            </ToggleButtonGroup>
            <Flex alignItems="flex-end" gap={1}>
              <Box width={260}>
                <Text as="div" small muted mb="4px" fontWeight="bold">
                  Source Agent
                </Text>
                {viewMode === 'table' ? (
                  <Field
                    name="selectedAgent"
                    options={this.agentOptions}
                    showLabel={false}
                    large
                    mb={0}
                    onChange={this.handleSingleAgentChange}
                    disabled={isPreview || this.agentOptions.length === 1}
                  >
                    <Select renderAsButton fill />
                  </Field>
                ) : (
                  <AgentSelectorCompact
                    mb={0}
                    agentOptions={this.agentOptions}
                    selectedAgents={selectedAgents}
                    showLabel={false}
                    large
                    disabled={isPreview || this.agentOptions.length === 1}
                    maxItems={MAX_TRACE_ITEMS}
                    onClose={this.handleAgentTargetClose}
                  />
                )}
              </Box>

              <Box width={260}>
                {this.targetOptions.length > 0 && !(viewMode === 'table' && this.isHostnameTargetedTest) && (
                  <>
                    <Text as="div" small muted mb="4px" fontWeight="bold">
                      Target
                    </Text>
                    {viewMode === 'table' ? (
                      <Field
                        name="selectedIp"
                        options={this.targetOptions}
                        showLabel={false}
                        large
                        mb={0}
                        onChange={this.handleSingleIpChange}
                        disabled={isPreview || this.targetOptions.length === 1}
                      >
                        <Select renderAsButton fill />
                      </Field>
                    ) : (
                      <Field
                        name="selectedIps"
                        options={this.targetOptions}
                        disabled={isPreview || this.targetOptions.length === 1}
                        large
                        mb={0}
                      >
                        <GroupedMultiSelect
                          unselectedGroupName="target"
                          optionFormatter={this.targetOptionFormatter}
                          maxItems={MAX_TRACE_ITEMS}
                          onClose={this.handleAgentTargetClose}
                        />
                      </Field>
                    )}
                  </>
                )}
              </Box>
            </Flex>
          </Flex>
          {viewMode === 'graph' && (
            <Flex flexDirection="column" alignItems="flex-end" gap={2}>
              <Flex alignItems="center">
                <Box mr={2}>
                  <Popover
                    minimal={false}
                    position={PopoverPosition.BOTTOM}
                    content={
                      <Box p={2}>
                        <Field name="groupBy" onChange={this.onGroupByChange} small>
                          <Select />
                        </Field>
                        {maxPathCount > 1 && (
                          <Box mx={1} mb={1}>
                            <MultiSlider min={0} max={maxPathCount} disabled={maxPathCount <= 1}>
                              <Handle
                                intentBefore="primary"
                                intentAfter="none"
                                value={collapseFromStart}
                                onChange={(val) => form.getField('collapseFromStart').onChange(val)}
                              />
                              <Handle
                                intentAfter="primary"
                                value={maxPathCount - collapseFromEnd}
                                onChange={(val) => form.getField('collapseFromEnd').onChange(maxPathCount - val)}
                              />
                            </MultiSlider>
                          </Box>
                        )}
                      </Box>
                    }
                  >
                    <Flex alignItems="center">
                      <Text as="div" mr="4px">
                        Group hops by
                      </Text>
                      <Button rightIcon="caret-down">{groupBtnText}</Button>
                    </Flex>
                  </Popover>
                </Box>
                <Flex alignItems="center">
                  <Text as="div" mr="4px">
                    Highlight
                  </Text>
                  <Box mr={1}>
                    <Popover
                      minimal={false}
                      position={PopoverPosition.BOTTOM}
                      content={
                        <Box p={1}>
                          <Flex alignItems="center" m={1}>
                            <Text as="div" whiteSpace="nowrap" mr={1} small>
                              When latency is at least
                            </Text>
                            <Field name="deltaLatencyThreshold" mb={0}>
                              <InputGroup type="number" min="0" rightElement={<Tag>ms</Tag>} width={100} />
                            </Field>
                          </Flex>
                        </Box>
                      }
                    >
                      <Button rightIcon="caret-down">Latency &gt;= {deltaLatencyThreshold}ms</Button>
                    </Popover>
                  </Box>
                  <Box>
                    <Popover
                      minimal={false}
                      position={PopoverPosition.BOTTOM}
                      content={
                        <Box p={1}>
                          <Flex justifyContent="space-between" alignItems="center">
                            <Box whiteSpace="nowrap" mr={1}>
                              When success rate is at or below
                            </Box>
                            <Field name="percSuccessThreshold" mb={0}>
                              <InputGroup type="number" min="0" max="100" rightElement={<Tag>%</Tag>} width={120} />
                            </Field>
                          </Flex>
                        </Box>
                      }
                    >
                      <Button rightIcon="caret-down">
                        {/* round the output to prevent precision goofiness such as 0.55 * 100 = 55.00000000000001  */}
                        Success Rate &lt;= {Math.round(percSuccessThreshold * 100)}%
                      </Button>
                    </Popover>
                  </Box>
                </Flex>
              </Flex>
              <Flex alignItems="center">
                <Field name="collapseTimeouts" showLabel={false} mb={0} mr={2}>
                  <Switch showFieldLabel />
                </Field>
                <Flex alignItems="center">
                  <Field name="moreThanExpected" showLabel={false} mb={0}>
                    <Switch showFieldLabel />
                  </Field>
                  <Tooltip content="Turn this on if you want us to compare latency measured with latency calculated using geo-ip distance">
                    <Icon iconSize={14} icon={FiInfo} color="primary" ml="4px" />
                  </Tooltip>
                </Flex>
                <Button onClick={this.resetForm} ml={2}>
                  Reset
                </Button>
              </Flex>
            </Flex>
          )}
        </Flex>
        {!hasResults && (
          <Card p={1} py={3}>
            <EmptyState icon="disable" title="No results" />
          </Card>
        )}
        {hasResults &&
          !!selectedAgents.length &&
          (viewMode === 'table' ? (
            <TracerouteTable
              traceroutes={traceroutes}
              selectedAgents={selectedAgents}
              selectedIps={this.isHostnameTargetedTest ? [] : selectedIps}
              isPreview={isPreview}
              $exports={$exports}
            />
          ) : (
            <Traceroute
              traceroutes={traceroutes}
              selectedAgents={selectedAgents}
              selectedIps={selectedIps}
              groupBy={groupBy}
              useLocalIp={useLocalIp}
              collapseFromStart={collapseFromStart}
              collapseFromEnd={collapseFromEnd}
              openPaths={openPaths}
              onOpenPaths={this.onOpenPaths}
              closeOpenPaths={this.closeOpenPaths}
              setPaths={this.setPaths}
              deltaLatencyThreshold={deltaLatencyThreshold}
              percSuccessThreshold={percSuccessThreshold}
              onNodeSelect={onNodeSelect}
              selectedNode={selectedNode}
              collapseTimeouts={collapseTimeouts}
              moreThanExpected={moreThanExpected}
              isPreview={isPreview}
            />
          ))}
      </Box>
    );
  }
}

export default TestPath;
